import {
  Alert,
  Button,
  ButtonActionProps,
  DataStatus,
  FieldValues,
  FormControl,
  FormField,
  showNotification,
} from 'platform/components';
import {Box, Show, Space, Stack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useEffect, useState} from 'react';
import {Path, useController, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCebiaFeatureDecoderConfigQuery} from '@dms/api/core';
import {useLazyGetAutofillFeaturesQuery} from '@dms/api/sales';
import {SimilarVehicleResponseBody} from '@dms/api/shared';
import {useLazyVinDecoderDecodeQuery, VinDecoderResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {AllOrNone, suffixTestId, TestIdProps} from 'shared/web';

import {stripDisallowedPlateChars, trimPlateSeparators, validateVin} from 'features/vin-validate';

import {useIsRegistrationPlateDecoderEnabled} from '../../hooks/useIsRegistrationPlateDecoderEnabled';
import {VehicleRecommendationType} from '../../types/VehicleRecommendationType';
import {handleApiError} from '../../utils/handleApiError';
import {SimilarVehicles} from './components/SimilarVehicles';
import {VehicleSelectionType} from './components/VehiclePreview';
import {useDecodeRegistrationPlate} from './hooks/useDecodeRegistrationPlate';
import {filterKeys} from './utils/filterKeys';
import {filterRegistrationPlateKeys} from './utils/filterRegistrationPlateKeys';
import {handleRegistrationPlateBlur} from './utils/handleRegistrationPlateBlur';
import {hasMeaningfulVinDecodeData} from './utils/hasMeaningfulVinDecodeData';
import {transformPaste} from './utils/transformPaste';
import {transformRegistrationPlatePaste} from './utils/transformRegistrationPlatePaste';

const FIELD_ROW_HEIGHT = 13;

type SimilarVehiclesDisplayProps = AllOrNone<{
  shouldRecommendVehiclesFor?: VehicleRecommendationType;
  onVehicleSelect: (
    vehicle: SimilarVehicleResponseBody,
    selectionType: VehicleSelectionType
  ) => void;
}> & {
  hideChooseButton?: boolean;
  excludeFromResultVehicleIds?: string[];
  additionalActions?: (vehicle: SimilarVehicleResponseBody) => ButtonActionProps[];
};

export type VinDecoderFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  vinField: {
    name: Path<TFieldValues>;
    label?: string;
    isDisabled?: boolean;
    isRecommended?: boolean;
    onVinDecode?: (vin: string) => void;
    onChange?: (value: string | null) => void;
  };
  registrationPlateField: {
    name: Path<TFieldValues>;
    label?: string;
    isDisabled?: boolean;
    isRecommended?: boolean;
  };
  isStatic?: boolean;
  onDecodedData?: (decodedData?: VinDecoderResponseBody) => void;
  isCebiaFeatureDecoderEnabled?: boolean;
  isSaving?: boolean;
} & TestIdProps &
  SimilarVehiclesDisplayProps;

export function VinDecoderField<TFieldValues extends FieldValues = FieldValues>(
  props: VinDecoderFieldProps<TFieldValues>
) {
  const [decodeVin] = useLazyVinDecoderDecodeQuery();
  const {data: {enabled: isCebiaFeatureDecoderEnabled} = {}} = useGetCebiaFeatureDecoderConfigQuery(
    undefined,
    {skip: !props.isCebiaFeatureDecoderEnabled}
  );
  const [getCebiaAutofillFeatures] = useLazyGetAutofillFeaturesQuery();

  const {decodeRegistrationPlate, isDecodingRegistrationPlate} = useDecodeRegistrationPlate();
  const {isRegistrationPlateDecoderEnabled, isLoading: isRegistrationPlateDecoderResolving} =
    useIsRegistrationPlateDecoderEnabled({isSkipped: props.isStatic});

  // formApi.setValue cannot be typed while TFieldValues is generic, hence a controller.
  const {field: vinFormField} = useController({
    control: props.control,
    name: props.vinField.name,
  });

  const [isDecoding, setIsDecoding] = useState(false);
  const [decodeStatus, setDecodeStatus] = useState<
    'full' | 'partial' | 'delayed' | 'error' | 'plateNotFound' | null
  >(null);
  const [delayedDecoding, setDelayedDecoding] = useState<{
    isDelayed: boolean;
    retries: number;
    vin: string | null;
    hasReceivedPartialData: boolean;
  }>({isDelayed: false, retries: 0, vin: null, hasReceivedPartialData: false});

  const resetDelayedDecoding = () =>
    setDelayedDecoding({isDelayed: false, retries: 0, vin: null, hasReceivedPartialData: false});

  // Also disables the buttons, not just isLoading: that is CSS only and leaves them keyboard-operable.
  const isDecodeInFlight = isDecodingRegistrationPlate || isDecoding || delayedDecoding.isDelayed;

  const mapDecodingStatusToAlert = (decodingStatus?: VinDecoderResponseBody['decodingStatus']) =>
    match<VinDecoderResponseBody['decodingStatus'], 'full' | 'partial' | 'delayed' | 'error'>(
      decodingStatus
    )
      .with('fully', () => 'full')
      .with('partially', () => 'partial')
      .with('delayed', () => 'delayed')
      .otherwise(() => 'error');

  const runDecode = useCallback(
    async (vin: string, isInitialAttempt: boolean = true) => {
      setIsDecoding(true);
      const decodedData = await decodeVin({vin}).unwrap().catch(handleApiError);

      if (decodedData) {
        const statusVariant = mapDecodingStatusToAlert(decodedData.decodingStatus);
        setDecodeStatus(statusVariant);
      }

      if (decodedData?.decodingStatus === 'delayed') {
        setDelayedDecoding((prev) => ({
          isDelayed: true,
          retries: prev.isDelayed ? prev.retries + 1 : 0,
          vin,
          hasReceivedPartialData:
            prev.hasReceivedPartialData || hasMeaningfulVinDecodeData(decodedData),
        }));
      } else {
        resetDelayedDecoding();
      }

      if (isCebiaFeatureDecoderEnabled && isInitialAttempt) {
        getCebiaAutofillFeatures({vin})
          .unwrap()
          .then((response) => {
            showNotification.success(i18n.t('entity.vehicle.notifications.featuresLoaded'));
            props.onDecodedData?.({...decodedData, features: response.features});
          })
          .catch(handleApiError)
          .finally(() => setIsDecoding(false));
      } else {
        props.onDecodedData?.(decodedData ?? undefined);
        setIsDecoding(false);
      }
    },
    [decodeVin, getCebiaAutofillFeatures, isCebiaFeatureDecoderEnabled, props]
  );

  const handleDecodeVin = async () => {
    if (props.isStatic) {
      return;
    }

    setDecodeStatus(null);

    const vin = props.formApi.getValues(props.vinField.name);

    if (isNil(vin)) {
      return props.formApi.setError(props.vinField.name, {
        message: i18n.t('entity.vehicle.notifications.vinIsRequired'),
      });
    }

    if (!validateVin(vin)) {
      return props.formApi.setError(props.vinField.name, {
        message: i18n.t('entity.vehicle.notifications.vinIsInvalid'),
      });
    }

    // After the guards: the vehicle form machine has no way out of the state this event starts.
    props.vinField?.onVinDecode?.(vin);

    try {
      resetDelayedDecoding();
      await runDecode(vin, true);
    } catch {
      resetDelayedDecoding();
      setDecodeStatus('error');
    }
  };

  const prefillDecodedVin = async (decodedVin: string) => {
    const vin = decodedVin.trim().toUpperCase();

    vinFormField.onChange(vin);
    // FormField fires this on every user edit; without it a form-level change listener never sees the VIN.
    props.control.onChange();
    props.vinField.onChange?.(vin);
    props.formApi.clearErrors(props.vinField.name);

    await handleDecodeVin();
  };

  const handleDecodeRegistrationPlate = async () => {
    if (props.isStatic) {
      return;
    }

    setDecodeStatus(null);

    const registrationPlate = trimPlateSeparators(
      stripDisallowedPlateChars(
        String(props.formApi.getValues(props.registrationPlateField.name) ?? '').toUpperCase()
      )
    );

    if (isNilOrEmpty(registrationPlate)) {
      return props.formApi.setError(props.registrationPlateField.name, {
        message: i18n.t('entity.vehicle.notifications.licencePlateIsRequired'),
      });
    }

    props.formApi.clearErrors(props.registrationPlateField.name);

    const outcome = await decodeRegistrationPlate(registrationPlate);

    await match(outcome)
      .with({type: 'vin'}, ({vin}) => prefillDecodedVin(vin))
      .with({type: 'notFound'}, () => setDecodeStatus('plateNotFound'))
      .with({type: 'error'}, () => undefined)
      .exhaustive();
  };

  useEffect(
    function pollDelayedDecoding() {
      if (!delayedDecoding.isDelayed) {
        return;
      }

      if (delayedDecoding.retries >= 2) {
        // After 3 total attempts (initial + 2 retries), fail
        const {hasReceivedPartialData} = delayedDecoding;
        resetDelayedDecoding();
        setDecodeStatus(hasReceivedPartialData ? 'partial' : 'error');
        return;
      }

      const timeoutId = setTimeout(() => {
        if (delayedDecoding.vin) {
          runDecode(delayedDecoding.vin, false);
        }
      }, 60000);

      return () => clearTimeout(timeoutId);
    },
    [delayedDecoding, runDecode]
  );

  return (
    <VStack spacing={4}>
      <DataStatus isLoading={isRegistrationPlateDecoderResolving} minHeight={FIELD_ROW_HEIGHT}>
        <Stack direction={['column', 'column', 'row', 'row']} spacing={[6, 6, 4, 4]}>
          <FormField
            control={props.control}
            type="text"
            name={props.registrationPlateField.name}
            label={props.registrationPlateField.label}
            data-testid={suffixTestId('registrationPlate', props)}
            isRecommended={props.registrationPlateField.isRecommended}
            isDisabled={props.registrationPlateField.isDisabled}
            onChange={() => props.formApi.clearErrors(props.registrationPlateField.name)}
            onKeyDown={filterRegistrationPlateKeys}
            onPaste={transformRegistrationPlatePaste}
            onBlur={handleRegistrationPlateBlur}
          />
          <Show when={!props.isStatic && isRegistrationPlateDecoderEnabled}>
            <VStack>
              <Show onDesktop onNotebook>
                <Space vertical={5} />
              </Show>
              <Button
                title={i18n.t('general.labels.decode')}
                aria-label={i18n.t('entity.vehicle.actions.decodeLicencePlate')}
                onClick={handleDecodeRegistrationPlate}
                isDisabled={props.vinField.isDisabled || props.isSaving || isDecodeInFlight}
                isLoading={props.isSaving || isDecodeInFlight}
                data-testid={suffixTestId('decodeRegistrationPlate', props)}
              />
            </VStack>
          </Show>
          <Box flex={1}>
            <FormField
              control={props.control}
              type="text"
              maxLength={17}
              isCounterVisible
              name={props.vinField.name}
              label={props.vinField.label}
              isRecommended={props.vinField.isRecommended}
              onChange={(value) => {
                props.formApi.clearErrors(props.vinField.name);
                props.vinField.onChange?.(value);
              }}
              onKeyDown={filterKeys}
              onPaste={transformPaste}
              isDisabled={props.vinField.isDisabled}
              data-testid={suffixTestId('vin', props)}
            />
          </Box>
          <Show when={!props.isStatic}>
            <VStack>
              <Show onDesktop onNotebook>
                <Space vertical={5} />
              </Show>
              <Button
                title={i18n.t('general.labels.decode')}
                onClick={handleDecodeVin}
                isDisabled={props.vinField.isDisabled || props.isSaving || isDecodeInFlight}
                isLoading={props.isSaving || isDecodeInFlight}
                data-testid={suffixTestId('decode', props)}
              />
            </VStack>
          </Show>
        </Stack>
      </DataStatus>
      <Show
        when={
          isNotNil(props.shouldRecommendVehiclesFor) && isNotNil(props.onVehicleSelect)
            ? {
                shouldRecommendVehiclesFor: props.shouldRecommendVehiclesFor,
                onVehicleSelect: props.onVehicleSelect,
              }
            : null
        }
      >
        {(similar) => (
          <SimilarVehicles
            control={props.control}
            registrationPlateField={props.registrationPlateField}
            shouldRecommendVehiclesFor={similar.shouldRecommendVehiclesFor}
            vinField={props.vinField}
            onVehicleSelect={similar.onVehicleSelect}
            hideChooseButton={props.hideChooseButton}
            excludeFromResultVehicleIds={props.excludeFromResultVehicleIds}
            additionalActions={props.additionalActions}
            data-testid={suffixTestId('SimilarVehicles', props)}
          />
        )}
      </Show>
      <Show when={decodeStatus === 'full'}>
        <Alert
          variant="success"
          title={i18n.t('entity.vehicle.notifications.vinDecodedSuccessfully')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedSuccessfullyDescription')}
          data-testid={suffixTestId('vinDecodedSuccessfully', props)}
        />
      </Show>
      <Show when={decodeStatus === 'partial'}>
        <Alert
          variant="warning"
          title={i18n.t('entity.vehicle.notifications.vinDecodedPartially')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedPartiallyDescription')}
          data-testid={suffixTestId('vinDecodedPartially', props)}
        />
      </Show>
      <Show when={decodeStatus === 'delayed'}>
        <Alert
          variant="warning"
          title={i18n.t('entity.vehicle.notifications.vinDecodedDelayed')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedDelayedDescription')}
          data-testid={suffixTestId('vinDecodedDelayed', props)}
        />
      </Show>
      <Show when={decodeStatus === 'error'}>
        <Alert
          variant="error"
          title={i18n.t('entity.vehicle.notifications.vinDecodedError')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedErrorDescriptions')}
          data-testid={suffixTestId('vinDecodedError', props)}
        />
      </Show>
      <Show when={decodeStatus === 'plateNotFound'}>
        <Alert
          variant="warning"
          title={i18n.t('entity.vehicle.notifications.licencePlateNotDecoded')}
          message={i18n.t('entity.vehicle.notifications.licencePlateNotDecodedDescription')}
          data-testid={suffixTestId('licencePlateNotDecoded', props)}
        />
      </Show>
    </VStack>
  );
}
